import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sun, Moon, User, ChevronDown, LogOut, Scale, Settings as SettingsIcon, Home, BookOpen } from 'lucide-react';
import GlobalSelectors from './GlobalSelectors';
import { useTheme } from '../contexts/ThemeContext';
import { useStytchAuth } from '../hooks/useStytchAuth';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { navigation } from '../utils/designTokens';
import { DOCS_URL } from '../config';

/**
 * Header component - top navigation bar
 *
 * Layout:
 * - Clickable logo on the left (navigates to Home)
 * - GlobalSelectors + Theme toggle + User menu on the right
 */
const Header = ({ activeAppSelector = true }) => {
  const { isDark, toggleTheme, nextDefaultTheme, nextTheme } = useTheme();
  const { getUserDisplayName, getOrganizationDisplayName, logout, member } = useStytchAuth();
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useTimeRange();
  const navigate = useNavigate();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleThemeClick = (event) => {
    toggleTheme({ includeAll: event.ctrlKey || event.metaKey });
  };

  // Build query string for navigation
  const queryString = (() => {
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    return params.toString() ? `?${params.toString()}` : '';
  })();

  return (
    <header
      className="flex items-center justify-between pl-6 pr-6 py-3"
      style={{
        backgroundColor: 'var(--color-header-bg)',
        borderBottom: '1px solid var(--color-border-subtle)',
      }}
    >
      {/* Logo + Home icon on the left */}
      <div className="flex items-center gap-3">
        <div className="flex items-center">
          <span
            className="text-xl font-bold tracking-tight"
            style={{ color: 'var(--color-text-heading)' }}
          >
            Lumoz
          </span>
          <span
            className="text-xl font-light ml-0.5"
            style={{ color: 'var(--color-accent-primary)' }}
          >
            AI
          </span>
        </div>
        <Link
          to={`/home${queryString}`}
          className="flex items-center p-1.5 rounded-md hover:bg-background-tertiary transition-colors"
          style={{ marginTop: '4px' }}
          title="Home"
          aria-label="Home"
        >
          <Home size={18} style={{ color: 'var(--color-text-muted)' }} />
        </Link>
      </div>

      {/* Right side: Global selectors + Theme toggle + User menu */}
      <div className="flex items-center gap-2.5">
        <GlobalSelectors activeAppSelector={activeAppSelector} />

        {/* Theme Toggle */}
        <div className="flex flex-col items-start px-1 py-2">
          <span className="block text-[10px] text-text-secondary uppercase tracking-wide mb-0.5">Theme</span>
          <button
            onClick={handleThemeClick}
            className="px-2.5 py-1 rounded-full bg-background-tertiary text-text-primary hover:bg-background-quaternary transition-colors"
            title={`Switch to ${nextDefaultTheme?.label || 'default theme'} (Ctrl/Cmd-click: ${nextTheme?.label || 'next theme'})`}
            aria-label={`Switch to ${nextDefaultTheme?.label || 'default theme'}`}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        {/* User Menu */}
        <div className="flex flex-col items-start px-1 py-2">
          <span
            className="block text-[10px] uppercase tracking-wide mb-0.5 select-none"
            style={{ color: 'transparent' }}
            aria-hidden="true"
          >
            User
          </span>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-background-tertiary transition-colors"
            >
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: navigation.user.avatar.size,
                  height: navigation.user.avatar.size,
                  backgroundColor: navigation.user.avatar.background,
                  borderRadius: navigation.user.avatar.borderRadius,
                }}
              >
                <User size={14} className="text-dark" />
              </div>
              <ChevronDown size={12} className={`text-text-muted transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {isUserMenuOpen && (
              <div
                className="absolute top-full right-0 mt-1 shadow-lg z-50 min-w-[200px]"
                style={{
                  backgroundColor: navigation.user.menu.background,
                  border: navigation.user.menu.border,
                  borderRadius: navigation.user.menu.borderRadius,
                }}
              >
                {/* User info header */}
                <div
                  className="px-3 py-2"
                  style={{ borderBottom: `1px solid var(--color-border-subtle)` }}
                >
                  <div
                    className="font-medium truncate"
                    style={{
                      fontSize: navigation.user.text.name.fontSize,
                      fontWeight: navigation.user.text.name.fontWeight,
                      color: navigation.user.text.name.color,
                    }}
                  >
                    {getUserDisplayName()}
                  </div>
                  <div
                    className="truncate"
                    style={{
                      fontSize: navigation.user.text.org.fontSize,
                      color: navigation.user.text.org.color,
                    }}
                  >
                    {getOrganizationDisplayName()}
                  </div>
                </div>

                {/* Menu items */}
                <Link
                  to={`/settings${queryString}`}
                  className="flex items-center text-text-primary hover:bg-background-secondary transition-colors"
                  style={{ padding: navigation.user.menu.padding, fontSize: navigation.user.menu.itemFontSize }}
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <SettingsIcon size={14} className="mr-2" />
                  Settings
                </Link>
                <a
                  href={DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-text-primary hover:bg-background-secondary transition-colors"
                  style={{ padding: navigation.user.menu.padding, fontSize: navigation.user.menu.itemFontSize }}
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <BookOpen size={14} className="mr-2" />
                  Documentation
                </a>
                <Link
                  to="/licenses"
                  className="flex items-center text-text-primary hover:bg-background-secondary transition-colors"
                  style={{ padding: navigation.user.menu.padding, fontSize: navigation.user.menu.itemFontSize }}
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Scale size={14} className="mr-2" />
                  Licenses
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center text-text-primary hover:bg-background-secondary transition-colors"
                  style={{ padding: navigation.user.menu.padding, fontSize: navigation.user.menu.itemFontSize }}
                >
                  <LogOut size={14} className="mr-2" />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
